import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import PatientDetailsHeader from '../../../../components/Account/PatientDetailsHeader';
import BackButton from '../../../../components/BackButton';

import TableRow from '@mui/material/TableRow';
import { useSelector } from 'react-redux';
import PressureTimelineCard from '../../../../components/Cards/PressureTimelineCard';
import { PressureTimelineData } from '../../../../components/PressureTimelineViewer/PressureTimelineViewer';
import {
  selectedTestDateTime,
  selectedTestTableData,
  selectedTestTableDataIsLoading,
} from '../../../../redux/selectors/patientSelectors';
import { activeTestService } from '../../../../services/activeTestService';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';

import { Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import IconLoader from '../../../../components/IconLoader';
import NoData from '../../../../components/NoData';
import PathCircularProgress from '../../../../components/PathCircularProgress';
import Scrollable from '../../../../components/Scrollable';
import { zip } from '../../../../helpers/utilityFunctions/utilityFunctions';
import { TestDataCategoryType } from '../../../../model/testDataCategoryType';
import {
  clearSelectedTestData,
  getPatientTestTableDataByCategory,
  setSelectedTestDate,
} from '../../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../../redux/configureStore';

const useStyles = makeStyles((theme: any) => ({
  tableCellHeader: {
    '&.MuiTableCell-root': {
      height: '48px',
      width: '60px',
      padding: '0px 10px 0px 10px',
      textAlign: 'center',
      fontWeight: 700,
    },
  },
  metricNameCell: {
    '&.MuiTableCell-root': {
      height: '48px',
      padding: '0px 10px 0px 10px',
      textAlign: 'center',
      fontWeight: 700,
    },
  },
  tableCell: {
    '&.MuiTableCell-root': {
      padding: '0px 10px 0px 10px',
      width: '60px',
      height: '24px',
      textAlign: 'center',
    },
  },
  emptyCell: {
    '&.MuiTableCell-root': {
      padding: '0px',
      width: '60px',
      height: '40px',
    },
  },
  headerText: {
    color: 'white',
    transform: 'rotate(-90deg)',
  },
  pageHeader: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(1),
  },
  pageTitle: {
    marginTop: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  pressurePlayer: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  centeredLoader: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
  noData: {
    marginTop: theme.spacing(4),
  },
  categoryRow: {
    display: 'flex',
    flexDirection: 'row',
    margin: '20px',
  },
  categoryLabel: {
    width: 30,
    borderRadius: '10px 0px 0px 10px',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  categoryTableContainer: {
    width: '130px',
    minWidth: '130px',
    maxWidth: '130px',
    flexShrink: 0,
  },
  loadingPaper: {
    width: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dataPaper: {
    display: 'flex',
    alignItems: 'flex-start',
    overflow: 'auto',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function getActiveTestTitle(activeType: string) {
  switch (activeType) {
    case '_CTEST':
      return 'Custom Test';
    case '_10MWT':
    default:
      return '10 Metre Walk Test';
  }
}

export function parseRawTestPayload(raw: unknown): any {
  if (typeof raw !== 'string') return raw;

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parseRawTestPayload(parsed) : parsed;
  } catch {
    return raw;
  }
}

function hasPressureSamples(value: any): boolean {
  return (
    value &&
    Array.isArray(value.data) &&
    value.data.some(
      (entry: any) =>
        typeof entry?.ts === 'number' && Array.isArray(entry?.semiRawImuPs?.ps)
    )
  );
}

export function hasPressurePlayerMask(value: any): boolean {
  // Florien - Add more complex mask check code here
  return Number(value?.mask) === 4096;
}

function normalizeInsoleData(value: any) {
  return {
    ...value,
    data: value.data.filter(
      (entry: any) =>
        typeof entry?.ts === 'number' && Array.isArray(entry?.semiRawImuPs?.ps)
    ),
  };
}

export function normalizePressureTimelineData(
  raw: unknown
): PressureTimelineData | null {
  const payload = parseRawTestPayload(raw);
  const candidates = [
    payload,
    payload?.name,
    payload?.data,
    payload?.name?.data,
  ];

  const match = candidates.find(
    (candidate) =>
      hasPressureSamples(candidate?.leftInsole) &&
      hasPressureSamples(candidate?.rightInsole)
  );

  if (!match) return null;

  return {
    ...match,
    leftInsole: normalizeInsoleData(match.leftInsole),
    rightInsole: normalizeInsoleData(match.rightInsole),
  };
}

export function shouldRenderPressurePlayer(raw: unknown): boolean {
  const payload = parseRawTestPayload(raw);
  const candidates = [
    payload,
    payload?.name,
    payload?.data,
    payload?.name?.data,
  ];

  return candidates.some(hasPressurePlayerMask);
}

const TenMetreWalkDetail: React.FC = () => {
  const { patientId, testGuid, testType } = useParams();
  const classes = useStyles();
  const dispatch = useAppThunkDispatch();
  const tableData = useSelector(selectedTestTableData);
  const isLoading = useSelector(selectedTestTableDataIsLoading);
  const location = useLocation();
  const { testDateTime: locationTestDateTime } =
    (location.state as { testDateTime?: string }) ?? {};
  const reduxTestDateTime = useSelector(selectedTestDateTime);
  const testDateTime = locationTestDateTime || reduxTestDateTime;
  const activeType = testType || '_10MWT';

  const [pressureData, setPressureData] = useState<PressureTimelineData | null>(
    null
  );
  const [rawTestDataIsLoading, setRawTestDataIsLoading] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (!testGuid) {
      setNoDataMessage(
        'No test was specified in the URL, so this detail cannot be loaded.'
      );
      return;
    }

    setNoDataMessage(null);
    setRawTestDataIsLoading(false);
    dispatch(clearSelectedTestData());
    if (locationTestDateTime)
      dispatch(setSelectedTestDate(locationTestDateTime));

    if (activeType === '_CTEST') {
      setRawTestDataIsLoading(true);
      activeTestService
        .DownloadActiveTestDataFile(testGuid, activeType)
        .then((raw: any) => {
          if (!isMounted) return;
          const payload = parseRawTestPayload(raw);
          const nextPressureData = shouldRenderPressurePlayer(payload)
            ? normalizePressureTimelineData(payload)
            : null;

          setPressureData(nextPressureData);
          if (!nextPressureData) {
            setNoDataMessage(
              'No pressure data was recorded for this custom test.'
            );
          }
        })
        .catch(() => {
          if (isMounted) {
            setNoDataMessage('No data was found for this custom test.');
          }
        })
        .finally(() => {
          if (isMounted) {
            setRawTestDataIsLoading(false);
          }
        });
    } else {
      dispatch(
        getPatientTestTableDataByCategory(
          testGuid,
          locationTestDateTime || reduxTestDateTime || '',
          activeType
        )
      );

      activeTestService
        .DownloadActiveTestDataFile(testGuid, activeType)
        .then((raw: any) => {
          if (!isMounted) return;
          const nextPressureData = normalizePressureTimelineData(raw);
          setPressureData(nextPressureData);
          if (!nextPressureData) {
            setNoDataMessage('No pressure data was found for this test.');
          }
        })
        .catch(() => {
          if (isMounted) {
            setPressureData(null);
          }
        });
    }

    return () => {
      isMounted = false;
      dispatch(clearSelectedTestData());
      setPressureData(null);
      setRawTestDataIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testGuid, activeType]);

  const categoryKeys = Object.keys(TestDataCategoryType).filter((key: any) =>
    isNaN(Number(key))
  );
  const visibleCategoryKeys = categoryKeys.filter((parentKey) => {
    if (!tableData) return false;
    return Object.entries(tableData).some(([key, value]: any) => {
      const keyMatches = key.toLowerCase().includes(parentKey.toLowerCase());
      const hasCategoryRows =
        value && typeof value === 'object' && Object.keys(value).length > 0;
      return keyMatches && hasCategoryRows;
    });
  });
  const shouldShowNoData =
    !isLoading &&
    !rawTestDataIsLoading &&
    (Boolean(noDataMessage) ||
      (activeType === '_CTEST'
        ? !pressureData
        : visibleCategoryKeys.length === 0));
  const resolvedNoDataMessage =
    noDataMessage ||
    (activeType === '_CTEST'
      ? 'No pressure data was recorded for this custom test.'
      : 'No table data was found for any test data category for this test.');

  return (
    <Box>
      <PatientDetailsHeader selectedPatientId={patientId} />
      <BackButton />
      <Box className={classes.pageHeader}>
        <Typography variant="h5" className={classes.pageTitle}>
          <strong>
            {getActiveTestTitle(activeType)}
            {testDateTime ? ' -' : ''}
          </strong>
        </Typography>
        {testDateTime && (
          <Typography variant="h5" className={classes.pageTitle}>
            {new Date(testDateTime).toLocaleString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}
          </Typography>
        )}
      </Box>
      {pressureData && (
        <Box className={classes.pressurePlayer}>
          <PressureTimelineCard data={pressureData} />
        </Box>
      )}
      {shouldShowNoData ? (
        <Box className={classes.noData}>
          <NoData message={resolvedNoDataMessage} />
        </Box>
      ) : rawTestDataIsLoading ? (
        <Box className={classes.centeredLoader}>
          <PathCircularProgress />
        </Box>
      ) : activeType === '_CTEST' ? null : isLoading &&
        visibleCategoryKeys.length === 0 ? (
        <Box className={classes.centeredLoader}>
          <PathCircularProgress />
        </Box>
      ) : (
        <Scrollable>
          {visibleCategoryKeys.map((parentKey: any) => {
            const categoryResults =
              tableData?.[
                Object.keys(tableData).find((key) =>
                  key.toLowerCase().includes(parentKey.toLowerCase())
                ) || ''
              ];
            const categoryEntries = categoryResults
              ? Object.entries(categoryResults)
              : [];

            return (
              <Box className={classes.categoryRow} key={parentKey}>
                <Box className={classes.categoryLabel}>
                  <Typography
                    className={classes.headerText}
                    component="h1"
                    variant="h5"
                  >
                    {parentKey}
                  </Typography>
                </Box>
                <TableContainer component={Paper} className={classes.categoryTableContainer}>
                  <Table aria-label="simple table">
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.emptyCell} />
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellHeader}>
                          metric
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          unit
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          foot
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          mean
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          median
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>sd</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>iqr</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>max</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>min</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          ratio
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}>cv</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {isLoading ? (
                  <Paper className={classes.loadingPaper}>
                    <PathCircularProgress />
                  </Paper>
                ) : (
                  <>
                    <Paper className={classes.dataPaper}>
                      {categoryEntries.map(([key, value]: any) => {
                        return (
                          <TableContainer key={key} style={{ width: 'fit-content', flexShrink: 0 }}>
                            <Table aria-label="simple table">
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    className={classes.tableCell}
                                    colSpan={2}
                                  >
                                    <IconLoader name={value.metric} />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    className={classes.metricNameCell}
                                    colSpan={2}
                                  >
                                    {value.metric}
                                  </TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell
                                    className={classes.tableCell}
                                    colSpan={2}
                                  >
                                    {value.unit}
                                  </TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell className={classes.tableCell}>
                                    L
                                  </TableCell>
                                  <TableCell className={classes.tableCell}>
                                    R
                                  </TableCell>
                                </TableRow>
                                {(() => {
                                  const fields = [
                                    'mean',
                                    'median',
                                    'sd',
                                    'iqr',
                                    'max',
                                    'min',
                                    'ratio',
                                    'cv',
                                  ] as const;
                                  const result = zip(
                                    fields.map((f) => value.foot.left[f]),
                                    fields.map((f) => value.foot.right[f])
                                  );
                                  return (
                                    result &&
                                    result.map((entry, index) => (
                                      <StyledTableRow key={index}>
                                        <TableCell
                                          className={classes.tableCell}
                                        >
                                          {entry[0]}
                                        </TableCell>
                                        <TableCell
                                          className={classes.tableCell}
                                        >
                                          {entry[1]}
                                        </TableCell>
                                      </StyledTableRow>
                                    ))
                                  );
                                })()}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        );
                      })}
                    </Paper>
                  </>
                )}
              </Box>
            );
          })}
        </Scrollable>
      )}
    </Box>
  );
};

export default TenMetreWalkDetail;
